import { inject, Injectable } from '@angular/core';
import { HisAuthService } from '../../../auth/service/his-auth.service';
import { HisUserProfileService } from './his-userprofile.service';

export interface PersistentValueKey { sessionStorage: string; userProfile: string; }

@Injectable( { providedIn: 'root' } )
export class HisPersistenceService {
    private readonly userProfileService = inject( HisUserProfileService );
    private readonly authService = inject( HisAuthService );

    /**
     * Reads a value from the user profile (or sessionStorage for anonymous users) for the given key.\
     * UserProfile values are only looked for in the client cache, so this method does not trigger a backend call and returns the value synchronously.
     * @param key the persistence key
     * @param T subtype of string; can be used to narrow the returned values type, e.g. for specific string literal types
     * @returns the value associated with the key, or null if not found
     */
    public readValue<T extends string = string>( key: PersistentValueKey ): T | null {
        if ( this.authService.isAnonymous ) {
            return sessionStorage.getItem( key.sessionStorage ) as T | null;
        }
        return this.userProfileService.getCachedValueForKey( key.userProfile ) as T | null;
    }

    /**
     * Writes a value in the applicable storage (user profile for authenticated users, sessionStorage for anonymous users).
     * If value is null, the entry is removed from the storage.
     * 
     * As the userProfileService methods are asynchronous, this method returns a promise that resolves when the value has been written to the storage,\
     * allowing to wait for the completion of the write operation if needed, to ensure that subsequent reads reflect the updated value.
     * 
     * If the value to be written is the same as the currently stored value, no write operation is performed and the returned promise resolves immediately.\
     * This prevents unnecessary write operations and backend calls.
     * @param key the persistence key
     * @param value the value to store
     * @returns a promise that resolves when the value has been written to the storage
     */
    public async persistValue( key: PersistentValueKey, value: string | null ): Promise<void> {
        if ( this.authService.isAnonymous ) {
            return value === null ? sessionStorage.removeItem( key.sessionStorage ) : sessionStorage.setItem( key.sessionStorage, value );
        }

        if ( this.readValue( key ) === value ) {
            // value is already up to date, no need to write
            console.debug( `[HisPersistenceService] Value for key '${key.userProfile}' is already up to date, skipping write operation.` );
            return;
        }

        return new Promise<void>( ( resolve , error ) => {
            const listener = { next: () => resolve(), error };
            if ( value === null ) {
                this.userProfileService.delete( key.userProfile ).subscribe( listener );
                return;
            }
            this.userProfileService.setNewValue( key.userProfile, value, null ).subscribe( listener );
        } );
    }

    /** Shorthand for deleting a persistent value via the `persistValue` method. */
    public async deleteValue( key: PersistentValueKey ): Promise<void> {
        return this.persistValue( key, null );
    }
}